import React, { Fragment } from 'react';

import PropTypes from 'prop-types';

import { DownloadTheApp } from 'wanderu-app-gettheapp';
import Footer from 'wanderu-component-footer';
import Helmet from 'wanderu-component-helmet';
import A from 'wanderu-component-react-router-link';
import {
    Section,
    ValueProps,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import c2cConfig from '../../../config/c2c.locale';
import ModeComparison from './ModeComparison/ModeComparison';
import MultiModalCityToCityHeader from './MultiModalCityToCityHeader/MultiModalCityToCityHeader';
import { TopCarriers } from './TopCarriers/TopCarriers';
import TravelOptionsCarousel from './TravelOptionsCarousel/TravelOptionsCarousel';
import { Trips } from './Trips/Trips';
import Trustpilot from './Trustpilot/Trustpilot';

import styles from './MultiModalCityToCity.scss';

const propTypes = {
    arrivePlace: PropTypes.string.isRequired,
    carousel: PropTypes.shape({
        slides: PropTypes.arrayOf(
            PropTypes.shape({
                href: PropTypes.string,
                imageCaption: PropTypes.string,
                imageUrl: PropTypes.string,
                title: PropTypes.string,
            })
        ),
        title: PropTypes.string,
    }),
    carriers: PropTypes.arrayOf(
        PropTypes.shape({
            carrierCode: PropTypes.string,
            name: PropTypes.string,
            pageSlug: PropTypes.string,
        })
    ),
    departPlace: PropTypes.string.isRequired,
    destinationPlaceId: PropTypes.string.isRequired,
    headerImageUrl: PropTypes.string,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }).isRequired,
    id: PropTypes.number,
    isFetchingAllTrips: PropTypes.bool,
    locale: PropTypes.string,
    locationData: PropTypes.shape({
        arrival: PropTypes.shape({
            formattedAddress: PropTypes.string,
            placeId: PropTypes.string,
        }),
        departure: PropTypes.shape({
            formattedAddress: PropTypes.string,
            placeId: PropTypes.string,
        }),
    }),
    metaDescription: PropTypes.string,
    metaTitle: PropTypes.string,
    modeData: PropTypes.shape({
        avg_bus_duration: PropTypes.number,
        avg_bus_price_usd: PropTypes.number,
        avg_daily_bus_trips: PropTypes.number,
        avg_daily_train_trips: PropTypes.number,
        avg_train_duration: PropTypes.number,
        avg_train_price_usd: PropTypes.number,
        cheapest: PropTypes.arrayOf(PropTypes.oneOf(['bus', 'train', 'flight', 'auto'])),
        fastest: PropTypes.arrayOf(PropTypes.oneOf(['bus', 'train', 'flight', 'auto'])),
        fastest_pre_departure: PropTypes.arrayOf(PropTypes.oneOf(['bus', 'train', 'flight', 'auto'])),
    }),
    noIndex: PropTypes.bool,
    originPlaceId: PropTypes.string.isRequired,
    path: PropTypes.string,
    searchParams: PropTypes.shape({
        arrivePlace: PropTypes.string,
        arrivePlaceId: PropTypes.string,
        departDate: PropTypes.string,
        departPlace: PropTypes.string,
        departPlaceId: PropTypes.string,
    }),
    showTrips: PropTypes.bool,
    t: PropTypes.func,
    trips: PropTypes.array,
    widgetTitle: PropTypes.string,
};

const defaultProps = {
    headerImageUrl: '',
    id: undefined,
    metaDescription: undefined,
    metaTitle: undefined,
    noIndex: false,
    path: undefined,
    searchParams: {},
    widgetTitle: undefined,
};

function ToCarouselCard({
    // eslint-disable-next-line react/prop-types
    href, imageUrl, title,
}) {
    return (
        <A href={href}>
            <div className={styles.toCard}>
                <img
                    alt=""
                    className={styles.toCardImage}
                    src={imageUrl}
                />
                <span className={styles.toTitle}>
                    {title}
                </span>
            </div>
        </A>
    );
}
function MultiModalCityToCity(props) {
    const {
        arrivePlace,
        carousel,
        carriers,
        departPlace,
        destinationPlaceId,
        headerImageUrl,
        history,
        id,
        isFetchingAllTrips,
        locale,
        locationData,
        metaDescription,
        metaTitle,
        modeData,
        noIndex,
        originPlaceId,
        path,
        searchParams,
        showTrips,
        t,
        trips,
        widgetTitle,
    } = props;

    const TO_CAROUSEL_DATA = carousel?.slides ?? [];
    const toCarouselData = TO_CAROUSEL_DATA.map(
        (carouselData) => (
            <ToCarouselCard
                {...carouselData}
                key={t(carouselData.label)}
                t={t}
            />
        ),
    );

    return (
        <Fragment>
            <Helmet
                hasHrefLangs={!noIndex}
                noFollow={noIndex}
                noIndex={noIndex}
                path={path}
                supportedLocales={noIndex
                    ? ['en-us']
                    : c2cConfig.supportedLocales}
            >
                <title>
                    {metaTitle || t(`meta-page-title-${id}`)}
                </title>
                <meta
                    content={metaDescription || t(`meta-page-description-${id}`)}
                    name="description"
                />
                <meta
                    content={metaDescription || t(`meta-page-description-${id}`)}
                    property="og:description"
                />
                <meta
                    content={metaTitle || t(`meta-page-title-${id}`)}
                    property="og:title"
                />
                <meta
                    content={metaDescription || t(`meta-page-description-${id}`)}
                    name="twitter:description"
                />
                <meta
                    content={metaTitle || t(`meta-page-title-${id}`)}
                    name="twitter:title"
                />
            </Helmet>
            <main>
                <MultiModalCityToCityHeader
                    arrivePlace={arrivePlace}
                    className={trips
                        ? styles.header
                        : ''}
                    departDate={searchParams && searchParams.departDate}
                    departPlace={departPlace}
                    destinationPlaceId={destinationPlaceId}
                    headerImageUrl={headerImageUrl}
                    hideHotelsAndApps={showTrips}
                    originPlaceId={originPlaceId}
                    title={widgetTitle || t(`widget-title-${id}`)}
                />
                {showTrips && <>
                    <div className={styles.gradient}>
                        <Trips
                            history={history}
                            isLoading={isFetchingAllTrips}
                            locale={locale}
                            searchParams={searchParams}
                            trips={trips}
                        />
                        <Section className={styles.valuePropsSection}>
                            <ValueProps
                                classes={{
                                    description: styles.hidden,
                                    gridItem: styles.valuePropsGridItem,
                                    icon: styles.valuePropsIcon,
                                    textColumn: styles.valuePropsTextColumn,
                                    title: styles.valuePropsTitle,
                                }}
                                titlePresent={false} />
                        </Section>
                    </div>
                    <Trustpilot />
                </>}
                {!showTrips && <Section className={styles.valuePropsSection}>
                    <ValueProps titlePresent={false} />
                </Section>}
                {(modeData && locationData)
                    && <ModeComparison
                        arrivePlace={arrivePlace}
                        data={modeData}
                        departPlace={departPlace}
                        history={history}
                        locationData={locationData}
                    />}
                {carriers && <TopCarriers carriers={carriers}></TopCarriers>}
                <TravelOptionsCarousel
                    carouselData={toCarouselData}
                    className={styles.toCarouselContainer}
                    label="carousel"
                    layoutClassName={styles.carouselSectionLayout}
                    title={carousel.title}
                    showArrows
                />
                {!showTrips && <Trustpilot />}
                <DownloadTheApp campaign="web-multi_modal_city_to_city" />
            </main>
            <Footer
                limitLinkFlow
            />
        </Fragment>
    );
}

MultiModalCityToCity.propTypes = propTypes;
MultiModalCityToCity.defaultProps = defaultProps;

export { MultiModalCityToCity };
export default localize(MultiModalCityToCity, ['t'], 'baseApp');
